import { isIOS } from '@lemonade-hq/cdk';
import { isNativeIOS } from '@lemonade-hq/uniclient-mobile-bridge';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { drawerContent as datePickerDrawer } from '../components/DatePicker/DatePicker.css';

const DEFAULT_KEYBOARD_HEIGHT_THRESHOLD = 100;

export interface UseMobileKeyboardHeightProps {
  readonly enabled?: boolean;
  readonly onChange?: (state: boolean) => void;
  readonly enabledThreshold?: number;
}

export interface UseMobileKeyboardHeight {
  readonly keyboardHeightRef: MutableRefObject<number>;
  readonly resetKeyboardHeight: () => void;
}

/* istanbul ignore next -- @preserve */
function isEligibleElement(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) return false;
  if (target.matches(`.${datePickerDrawer} *`)) return false;

  const tagName = target.tagName;
  const eligibleTags = ['INPUT', 'TEXTAREA'];

  const isContentEditable = target.isContentEditable;

  return eligibleTags.includes(tagName) || isContentEditable;
}

export const useMobileKeyboardHeight = ({
  enabled = true,
  onChange,
  enabledThreshold = DEFAULT_KEYBOARD_HEIGHT_THRESHOLD,
}: UseMobileKeyboardHeightProps): UseMobileKeyboardHeight => {
  const keyboardHeightRef = useRef(0);

  const resetKeyboardHeight = useCallback(() => {
    keyboardHeightRef.current = 0;
    onChange?.(false);
  }, [onChange]);

  useEffect(() => {
    if (!enabled) return;

    const initialWindowHeight = window.visualViewport?.height ?? window.outerHeight;

    /* istanbul ignore next -- @preserve - can't test visualViewport?.addEventListener */
    const onResize = (): void => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- onResize is only called when visualViewport is available
      const visualViewportHeight = Math.floor(window.visualViewport!.height);

      keyboardHeightRef.current = initialWindowHeight - visualViewportHeight;

      onChange?.(keyboardHeightRef.current > enabledThreshold);
    };

    window.visualViewport?.addEventListener('resize', onResize);

    // this is a hack!
    // since iOS resize event is not consistent when the keyboard opens,
    // we limit the focus out on an input to 500ms after click
    /* istanbul ignore next -- @preserve */
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (!isEligibleElement(target)) return;

      const forceFocus = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        target.focus();
      };

      document.body.addEventListener('click', forceFocus, { capture: true });

      setTimeout(() => {
        document.body.removeEventListener('click', forceFocus, { capture: true });
      }, 500);
    };

    /* istanbul ignore next -- @preserve */
    if ((isIOS() || isNativeIOS()) && window.isPlaywright == null) {
      document.addEventListener('focusin', onFocusIn, { capture: true });
    }

    return () => {
      window.visualViewport?.removeEventListener('resize', onResize);
      document.removeEventListener('focusin', onFocusIn, { capture: true });
    };
  }, [enabled, onChange, resetKeyboardHeight, enabledThreshold]);

  return {
    keyboardHeightRef,
    resetKeyboardHeight,
  };
};
